import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import clsx from 'clsx';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import { Scrollbar } from 'react-scrollbars-custom';
import useScrollToElement from '../../../../hooks/useScrollToElement';
import TabPanel from '../../../TabPanel';
import { getHotelRequest, getHotelSuccess } from '../../../../store/slices/hotel';
import OverviewDetails from './OverviewDetails';
import AboutDetails from './AboutDetails';
import AmenitiesDetails from './AmenitiesDetails';
import ImageGallery from '../../../ImageGallery';
import RoomsDetails from './RoomsDetails';
import Warning from '../../../../assets/svgs/Warning.svg';
import {
  checkDoSearchDeepLinkWithoutDate, PageLinks, SearchStatus, TravelTypes,
} from '../../../../constants';
import getSelectedHotelItinerary from '../../../../utils/getSelectedHotelItinerary';
import HotelDeals from './HotelDeals';
import MarkdownToHtml from '../../../MarkdownToHtml';
import prioritizeHotelRates from '../../../../helpers/prioritizeHotelRates';

dayjs.extend(dayjsPluginUTC);

const HotelDetails = ({
  itinerary, tripId, onModalClose, doAutoScrollToTab, doShowPrice, doShowAgentCommission,
  travelOption, tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { pageName } = router.query;
  const { travelAgency } = useSelector((store) => store.user);
  const hotelTabTitle = travelAgency && travelAgency.filterSettings
    && travelAgency.filterSettings.hotelDealsTabTitle;
  const doAllowDeepLinkWithoutDate = travelAgency && travelAgency.settings
  && travelAgency.settings.doAllowDeepLinkWithoutDate;
  const doShowHotelDeals = travelAgency && travelAgency.doShowHotelDeals;
  const selectedTabClasses = 'text-theme border-b-2 border-theme';
  const { hotel } = itinerary;
  const [selectedTab, setSelectedTab] = useState(1);
  const dispatch = useDispatch();
  const { isViewingCompare } = useSelector((store) => store.compare);
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const { selectedTrips } = useSelector((store) => store.trip);
  const trip = selectedTrips && selectedTrips[tripId];
  const storeHotelDetails = useSelector((store) => store.hotel.hotelDetails[itinerary.hotelKey]);
  const selectedHotelItinerary = trip
    && trip.hotelItineraries
    && getSelectedHotelItinerary(itinerary, trip.hotelItineraries);
  const isChangeAddHotelPage = pageName === PageLinks.ChangeHotel.pageName
    || pageName === PageLinks.AddHotel.pageName;
  const [showMoreClicked, setShowMoreClicked] = useState();
  const isShowMoreClicked = showMoreClicked === itinerary.hotelKey;
  const hotelDetails = (selectedHotelItinerary && !isChangeAddHotelPage && !isShowMoreClicked)
    ? selectedHotelItinerary
    : storeHotelDetails;
  const isAnyRateFromHotelAddedToTrip = Boolean(selectedHotelItinerary && !isChangeAddHotelPage);
  const customHotelTabContent = hotelDetails && hotelDetails.hotel
    && hotelDetails.hotel.customContents;
  const hotelRateCodes = useSelector((store) => store.user.hotelRateCodes);
  const isHotelLoaded = hotelDetails && hotelDetails.searchStatus !== SearchStatus.SEARCHING;
  const { track } = useAnalytics();
  const [isScrolled, setIsScrolled] = useState(false);
  const conditionValue = doAutoScrollToTab || isScrolled ? false : selectedTab;
  const hotelShopKey = hotelDetails && hotelDetails.hotel
    && hotelDetails.hotel.shopKeyForAdditionalRooms
    ? hotelDetails.hotel.shopKeyForAdditionalRooms : undefined;
  const overViewTab = customHotelTabContent && customHotelTabContent.find((tab) => tab.tabName === 'Overview');
  const aboutTab = customHotelTabContent && customHotelTabContent.find((tab) => tab.tabName === 'About');
  const doHideAboutTab = aboutTab && aboutTab.doHideTab;
  const amenitiesTab = customHotelTabContent && customHotelTabContent.find((tab) => tab.tabName === 'Amenities');
  const doHideAmenitiesTab = amenitiesTab && amenitiesTab.doHideTab;
  const isConfirmationPage = router.pathname.indexOf('confirmation') > -1;
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const isAddOrChangeSearchPage = router.pathname.startsWith('/search/');
  const travelOptionMode = travelOption && travelOption.mode;
  const handleScrolled = () => {
    setIsScrolled(true);
  };

  const hotelDetailsEl = useScrollToElement({ value: conditionValue }, handleScrolled);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    track('hotelDetails.handleTabChange', {
      index,
    });
  };

  const handleShowMoreClick = useCallback(() => {
    setShowMoreClicked(itinerary.hotelKey);

    dispatch(getHotelRequest({
      itinerary,
      tripId,
      isTravelOption: travelOptionMode === TravelTypes.hotel,
      currencyCode: isViewingCompare ? itinerary.cost.currencyCode : undefined,
      shopKeyForAdditionalRooms: hotelShopKey,
    }));
  }, [dispatch, tripId, itinerary, isViewingCompare, travelOptionMode, hotelShopKey]);

  useEffect(() => {
    if ((tripId || isViewingCompare) && !isViewingBrochure && !isBrochurePage
      && (travelOptionMode !== undefined || isAddOrChangeSearchPage)
      && !checkDoSearchDeepLinkWithoutDate(router.query, doAllowDeepLinkWithoutDate)) {
      dispatch(getHotelRequest({
        itinerary,
        tripId,
        isTravelOption: travelOptionMode === TravelTypes.hotel,
        currencyCode: isViewingCompare ? itinerary.cost.currencyCode : undefined,
      }));
    } else {
      dispatch(getHotelSuccess({ itinerary, response: itinerary }));
    }
  }, [dispatch, hotel, tripId, itinerary, isViewingCompare, travelOptionMode,
    router.query, doAllowDeepLinkWithoutDate, isViewingBrochure, isBrochurePage,
    isAddOrChangeSearchPage]);

  const tabs = [
    {
      title: overViewTab && overViewTab.displayTabName ? overViewTab.displayTabName : 'Overview',
      content: isHotelLoaded && hotelDetails && hotelDetails.hotel ? (
        <OverviewDetails
          hotelDetails={hotelDetails.hotel}
          customOverViewContent={overViewTab && overViewTab.tabContent}
        />
      ) : <></>,
    },
    {
      title: 'Rooms',
      content: (isHotelLoaded || isShowMoreClicked) && hotelDetails && hotelDetails.hotel ? (
        <>
          <RoomsDetails
            hotelDetails={prioritizeHotelRates(hotelDetails, travelAgency, hotelRateCodes)}
            tripId={tripId}
            onModalClose={onModalClose}
            doShowPrice={doShowPrice}
            doShowAgentCommission={doShowAgentCommission}
            tripRoomSearchInput={tripRoomSearchInput}
            isAnyRateFromHotelAddedToTrip={isAnyRateFromHotelAddedToTrip}
          />

          {isHotelLoaded && hotelShopKey && !isViewingBrochure && !isConfirmationPage
            && (
              <div>
                <button onClick={handleShowMoreClick} type="button" className="banner w-full rounded bg-orange-400 text-orange-800 p-2 sticky top-0 z-10 mb-2">
                  <span>Show more rates</span>
                </button>
              </div>
            )}
        </>
      ) : <></>,
    },
    {
      title: 'Photos',
      content: isHotelLoaded && hotelDetails && hotelDetails.hotel ? (
        <ImageGallery
          images={hotelDetails.hotel ? hotelDetails.hotel.images : undefined}
        />
      ) : <></>,
    },
  ];

  if (!doHideAboutTab) {
    const title = aboutTab && aboutTab.displayTabName ? aboutTab.displayTabName : 'About';
    const content = aboutTab && aboutTab.tabContent ? (
      <MarkdownToHtml content={aboutTab.tabContent} className="text-black" />
    ) : (isHotelLoaded && hotelDetails && hotelDetails.hotel && (
      <AboutDetails
        hotelDetails={hotelDetails.hotel}
        doShowAgentCommission={doShowAgentCommission}
      />
    ));
    tabs.push({ title, content });
  }

  if (!doHideAmenitiesTab) {
    let amenities = isHotelLoaded && hotelDetails
      && hotelDetails.hotel ? hotelDetails.hotel.amenities : [];
    if (amenitiesTab && amenitiesTab.tabContent) {
      const customAmenties = amenitiesTab.tabContent.split(/\s*,\s*/).filter((item) => item !== '');
      if (customAmenties && customAmenties.length) {
        amenities = customAmenties;
      }
    }
    tabs.push({
      title: amenitiesTab && amenitiesTab.displayTabName ? amenitiesTab.displayTabName : 'Amenities',
      content: amenities && amenities.length > 0 ? (
        <AmenitiesDetails hotelAmenities={amenities} />
      ) : <></>,
    });
  }

  if (customHotelTabContent && customHotelTabContent.length > 0) {
    customHotelTabContent.forEach((tab) => {
      if (tab && !tab.doHideTab && tab.tabContent && tab.tabName === 'Custom') {
        tabs.push({
          title: tab.displayTabName ? tab.displayTabName : tab.tabName,
          content: <MarkdownToHtml content={tab.tabContent} className="text-black" />,
        });
      }
    });
  }

  if (doShowHotelDeals) {
    tabs.push({
      title: `${hotelTabTitle || 'Deals'}`,
      content: isHotelLoaded && (
        <HotelDeals deals={hotelDetails && hotelDetails.hotelDeals} />
      ),
    });
  }

  return (
    <div ref={hotelDetailsEl}>
      <div className="mb-3 h-16">
        <Scrollbar className="w-full h-full" noScrollY>
          <div className="grid grid-flow-col auto-cols-max gap-5 border-b-2">
            {tabs.map((tab, index) => (
              <a
                key={tab.title}
                onClick={() => { handleTabChange(index); }}
                onKeyDown={() => { handleTabChange(index); }}
                role="button"
                tabIndex={tabs.length - index}
                className={clsx(selectedTab === index && selectedTabClasses, 'py-4 text-sm mt-auto md:text-base lg:min-w-24 text-center focus:outline-none block w-full')}
              >
                {tab.title}
              </a>
            ))}
          </div>
        </Scrollbar>
      </div>
      {(isHotelLoaded || isShowMoreClicked)
        && (
          hotelDetails && hotelDetails.hotel
            ? tabs.map((tab, index) => (
              <TabPanel value={selectedTab} index={index} key={tab.title}>
                {tab.content}
              </TabPanel>
            ))
            : (
              <div className="flex items-center flex-col h-40 justify-center gap-3 text-center">
                <Warning />
                Hotel detail and rooms not found!
              </div>
            )
        )}
      {!isHotelLoaded
        && (
          <div className="text-center">
            <CircularProgress color="secondary" />
          </div>
        )}
    </div>
  );
};

HotelDetails.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  tripId: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowPrice: PropTypes.bool,
  doAutoScrollToTab: PropTypes.bool,
  doShowAgentCommission: PropTypes.bool,
  travelOption: PropTypes.shape({
    mode: PropTypes.number,
    key: PropTypes.string,
  }),
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

HotelDetails.defaultProps = {
  tripId: '',
  onModalClose: undefined,
  doShowPrice: true,
  doAutoScrollToTab: false,
  doShowAgentCommission: true,
  travelOption: {},
  tripRoomSearchInput: undefined,
};

export default HotelDetails;
